import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Loading, Message, Page } from "../components/Page";
import { ApiError, api } from "../lib/api";
import type { OpenedPersonalLink, TakeView } from "../lib/types";
import { ResumeCard, StartForm, StartIntro } from "./Start";

/** Where this tab keeps a personal link's token once it's out of the address bar. */
const STORE = "gp_personal_link";

function recallToken(): string | null {
  try {
    return sessionStorage.getItem(STORE);
  } catch {
    return null;
  }
}
function keepToken(token: string): void {
  try {
    sessionStorage.setItem(STORE, token);
  } catch {
    // Storage blocked: the token stays in memory for this visit.
  }
}

/** What the link costs them, in a line under the intro. */
function priceLine(link: OpenedPersonalLink): string | null {
  if (link.fromCode) return "Your code is for this test, so it's free for you.";
  if (link.pricing === "free") return "It's free for you.";
  if (link.pricing === "discounted") {
    const d = link.discount;
    if (d?.percentOff) return `Your discount (${d.percentOff}% off) is applied at checkout.`;
    if (d?.amountOffCents) return `Your discount ($${(d.amountOffCents / 100).toFixed(2).replace(/\.00$/, "")} off) is applied at checkout.`;
    return "Your discount is applied at checkout.";
  }
  return null;
}

/**
 * /i#t=<token>: a personal link (made in the admin, by a staff key or a connected app). The
 * token is in the hash so it never reaches a server log or the ad pixels; it's sent to the API
 * in a POST body, kept for this tab, and taken out of the address bar. The start form opens with
 * their details filled in and their email fixed (the API only takes the link's).
 */
export function PersonalLink() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => {
    const fromHash = new URLSearchParams(location.hash.replace(/^#/, "")).get("t");
    if (fromHash) keepToken(fromHash);
    return fromHash || recallToken();
  });
  const [link, setLink] = useState<OpenedPersonalLink | null>(null);
  const [error, setError] = useState<"invalid" | "used" | "expired" | "failed" | null>(token ? null : "invalid");
  // Only a take started from this link: on a shared computer, never someone else's test.
  const [current, setCurrent] = useState<TakeView | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!token) return;
    let live = true;
    api
      .openPersonalLink(token)
      .then((l) => {
        if (!live) return;
        setLink(l);
        if (l.resume) {
          api
            .currentTake()
            .then((t) => live && setCurrent(t.status === "in_progress" ? t : null))
            .catch(() => {});
        }
      })
      .catch((e) => {
        if (!live) return;
        const code = e instanceof ApiError ? e.code : "";
        setError(code === "link_used" ? "used" : code === "link_expired" ? "expired" : code === "link_invalid" || (e instanceof ApiError && e.status === 404) ? "invalid" : "failed");
      });
    return () => {
      live = false;
    };
  }, [token]);

  if (error === "invalid") {
    return (
      <Message title="This link isn't valid">
        <p>Check you've copied the whole link.</p>
        <p className="mt-6 text-[15px]">
          <Link to="/">Go to the start</Link>
        </p>
      </Message>
    );
  }
  if (error === "used") {
    return (
      <Message title="This link has been used">
        <p>It's already been used to take the Gifts Profile. Your results are in the email we sent you.</p>
        <p className="mt-6 text-[15px]">
          <Link to="/sign-in">Sign in to see your profiles</Link>
        </p>
      </Message>
    );
  }
  if (error === "expired") {
    return (
      <Message title="This link has expired">
        <p>Ask whoever sent it for a new one.</p>
      </Message>
    );
  }
  if (error === "failed") {
    return (
      <Message title="Something went wrong">
        <p>We couldn't load this page. Check your connection and reload.</p>
      </Message>
    );
  }
  if (!link || !token) return <Loading />;

  return (
    <Page brand="center">
      <StartIntro kicker={link.fromCode ? "With your code" : link.firstName ? `For ${link.firstName}` : undefined} message={priceLine(link)} />
      {current && !showForm ? (
        <ResumeCard take={current} onStartOver={link.fromCode ? undefined : () => setShowForm(true)} />
      ) : (
        <StartForm personalLink={token} prefill={{ firstName: link.firstName, email: link.email, phone: link.phone }} />
      )}
    </Page>
  );
}
